import { useEffect, useState } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, ChevronDown, ChevronRight, Zap } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import { TitleCaseInput } from "@/components/ui/text-input";
import { planCreateSchema, tipoServicioEnum, type PlanCreateInput, type Plan } from "./schemas";
import { useCreatePlan, useUpdatePlan } from "./api";
import type { ApiError } from "@/lib/api";

const DEFAULTS: PlanCreateInput = {
  nombre: "",
  descripcion: "",
  tipo_servicio: "wireless",
  velocidad_down_mbps: 10,
  velocidad_up_mbps: 2,
  precio: 0,
  moneda: "MXN",
  rate_limit_profile: "",
  periodicidad_dias: 30,
  burst_down_mbps: "",
  burst_up_mbps: "",
  burst_threshold_down_mbps: "",
  burst_threshold_up_mbps: "",
  burst_time_s: "",
  reuso_ratio: 1,
  orden: 0,
  tags: [],
};

function toForm(p: Plan): PlanCreateInput {
  return {
    nombre: p.nombre,
    descripcion: p.descripcion ?? "",
    tipo_servicio: p.tipo_servicio,
    velocidad_down_mbps: p.velocidad_down_mbps,
    velocidad_up_mbps: p.velocidad_up_mbps,
    precio: Number(p.precio),
    moneda: p.moneda,
    rate_limit_profile: p.rate_limit_profile ?? "",
    periodicidad_dias: p.periodicidad_dias,
    burst_down_mbps: p.burst_down_mbps ?? "",
    burst_up_mbps: p.burst_up_mbps ?? "",
    burst_threshold_down_mbps: p.burst_threshold_down_mbps ?? "",
    burst_threshold_up_mbps: p.burst_threshold_up_mbps ?? "",
    burst_time_s: p.burst_time_s ?? "",
    reuso_ratio: p.reuso_ratio ?? 1,
    orden: p.orden,
    tags: p.tags ?? [],
  };
}

function clean<T>(v: T): T {
  if (typeof v === "string") return (v.trim() === "" ? undefined : v.trim()) as T;
  if (Array.isArray(v)) return v.map(clean) as T;
  if (v && typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v)) {
      const c = clean(val);
      if (c !== undefined) out[k] = c;
    }
    return out as T;
  }
  return v;
}

export function PlanFormDialog({
  open, onOpenChange, plan, onSaved,
}: {
  open: boolean; onOpenChange: (v: boolean) => void; plan?: Plan | null; onSaved?: (id: string) => void;
}) {
  const editMode = !!plan;
  const [error, setError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { register, handleSubmit, control, reset, formState: { errors } } = useForm<PlanCreateInput>({
    resolver: zodResolver(planCreateSchema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (open) {
      const values = plan ? toForm(plan) : DEFAULTS;
      reset(values);
      setError(null);
      // Abrir sección avanzada automáticamente si tiene burst o reuso != 1
      const hasAdvanced =
        !!plan?.burst_down_mbps ||
        !!plan?.burst_up_mbps ||
        !!plan?.burst_time_s ||
        (plan?.reuso_ratio ?? 1) !== 1;
      setShowAdvanced(hasAdvanced);
    }
  }, [open, plan, reset]);

  const createMut = useCreatePlan();
  const updateMut = useUpdatePlan(plan?.id ?? "");

  const onSubmit: SubmitHandler<PlanCreateInput> = async (values) => {
    setError(null);
    try {
      const cleaned = clean(values);
      const saved = editMode
        ? await updateMut.mutateAsync(cleaned)
        : await createMut.mutateAsync(cleaned);
      onSaved?.(saved.id);
      onOpenChange(false);
    } catch (e) {
      setError((e as ApiError).message ?? "No se pudo guardar el plan");
    }
  };

  const submitting = createMut.isPending || updateMut.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogHeader>
          <DialogTitle>{editMode ? "Editar plan" : "Nuevo plan"}</DialogTitle>
          <DialogDescription>
            Velocidades, precio y perfil MikroTik. El día de corte se elige al crear el contrato del cliente.
          </DialogDescription>
        </DialogHeader>
        <DialogContent>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Nombre *</Label>
              <TitleCaseInput control={control} name="nombre" placeholder="Plan Fibra 50/10" />
              {errors.nombre && <span className="text-xs text-destructive">{errors.nombre.message}</span>}
            </div>
            <div>
              <Label>Tipo de servicio</Label>
              <Select {...register("tipo_servicio")}>
                {tipoServicioEnum.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label>Precio mensual (MXN) *</Label>
              <Input type="number" step="0.01" {...register("precio")} />
              {errors.precio && <span className="text-xs text-destructive">{errors.precio.message}</span>}
            </div>
            <div>
              <Label>Velocidad bajada (Mbps) *</Label>
              <Input type="number" {...register("velocidad_down_mbps")} />
              {errors.velocidad_down_mbps && (
                <span className="text-xs text-destructive">{errors.velocidad_down_mbps.message}</span>
              )}
            </div>
            <div>
              <Label>Velocidad subida (Mbps) *</Label>
              <Input type="number" {...register("velocidad_up_mbps")} />
              {errors.velocidad_up_mbps && (
                <span className="text-xs text-destructive">{errors.velocidad_up_mbps.message}</span>
              )}
            </div>
            <div className="col-span-2">
              <Label>Rate limit profile (MikroTik, opcional)</Label>
              <Input
                {...register("rate_limit_profile")}
                placeholder="Si vacío, usa el nombre del plan como profile"
              />
            </div>
            <div className="col-span-2">
              <Label>Descripción</Label>
              <Textarea rows={2} {...register("descripcion")} />
            </div>
            <div className="col-span-2 -mt-2 text-[10px] text-muted-foreground">
              Los planes se cobran mensualmente. El día de corte se define por cliente al crear su contrato.
            </div>

            {/* === Sección avanzada: burst + reuso + orden === */}
            <div className="col-span-2 mt-2 rounded-md border bg-muted/20">
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="flex w-full items-center justify-between p-3 text-left text-xs font-semibold uppercase text-muted-foreground hover:bg-muted/40"
              >
                <span className="flex items-center gap-1.5">
                  <Zap size={12} />
                  Opciones avanzadas (ráfagas y reuso)
                </span>
                {showAdvanced ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              </button>

              {showAdvanced && (
                <div className="border-t p-3">
                  <div className="mb-3 text-[10px] text-muted-foreground">
                    Configura ráfagas (burst) para permitir velocidad pico temporal por encima de la nominal.
                    MikroTik aplica el burst mientras el promedio de los últimos <em>burst_time</em> segundos esté
                    debajo del umbral. Si los campos están vacíos, no hay burst.
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Burst bajada (Mbps)</Label>
                      <Input type="number" placeholder="Ej. 80 para plan de 50 Mbps" {...register("burst_down_mbps")} />
                    </div>
                    <div>
                      <Label>Burst subida (Mbps)</Label>
                      <Input type="number" placeholder="Ej. 15 para plan de 10 Mbps" {...register("burst_up_mbps")} />
                    </div>
                    <div>
                      <Label>Umbral bajada (Mbps)</Label>
                      <Input type="number" placeholder="Default: 75% velocidad" {...register("burst_threshold_down_mbps")} />
                    </div>
                    <div>
                      <Label>Umbral subida (Mbps)</Label>
                      <Input type="number" placeholder="Default: 75% velocidad" {...register("burst_threshold_up_mbps")} />
                    </div>
                    <div>
                      <Label>Burst time (segundos)</Label>
                      <Input type="number" placeholder="Default MikroTik: 8" {...register("burst_time_s")} />
                    </div>
                    <div>
                      <Label>Reuso / contention ratio</Label>
                      <Input
                        type="number"
                        min={1}
                        max={100}
                        {...register("reuso_ratio")}
                        placeholder="1 = dedicado, 4 = 1:4"
                      />
                      <span className="text-[10px] text-muted-foreground">
                        Informativo — no se aplica en rate-limit.
                      </span>
                    </div>
                    <div className="col-span-2">
                      <Label>Orden (display en listas)</Label>
                      <Input type="number" {...register("orden")} />
                      <span className="text-[10px] text-muted-foreground">
                        Cuando hay varios planes, controla el orden visual en dropdowns.
                        Default 0 = ordena alfabético.
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {error && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-2 text-xs text-destructive">
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </DialogContent>
        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" size="sm" disabled={submitting}>
            {submitting ? "Guardando…" : editMode ? "Guardar cambios" : "Crear plan"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
